import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { Loader } from '@components/Loader';
import { Spacer } from '@components/Spacer';
import { Sidebar } from '@insights_module/components/Sidebar';
import { EpisodeGarmSection } from '@insights_module/components/episode_details_screen/EpisodeGarmSection';
import { EpisodeContextualSection } from '@insights_module/components/episode_details_screen/EpisodeContextualSection';
import { EpisodeEntitiesSection } from '@insights_module/components/episode_details_screen/EpisodeEntitiesSection';
import { EpisodeTranscriptControls } from '@insights_module/components/episode_details_screen/EpisodeTranscriptControls';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { useEpisodeSegment } from '@insights_module/hooks/useEpisodeSegment';
import { useEpisodeDetailsData } from '@insights_module/hooks/useEpisodeDetailsData';

const Title = tw.span`block text-left text-text-and-icon text-[16px] font-semibold tracking-[0.16px] mb-[6.5px]`;
const Description = tw(
  Title
)`block text-text-and-icon-muted text-[14px] font-normal tracking-[0.14px] leading-[140%]`;
const Separator = tw.div`w-full h-[1px] bg-gray-1 my-4`;

export const EpisodeDetailsSidebar = () => {
  const { t } = useTranslation();
  const { activeView } = useEpisodeDetailsContext();
  const { segmentsWithParagraphs } = useEpisodeSegment();
  const { isLoading } = useEpisodeDetailsData();

  const episodeDetailsSidebarCopy = useMemo(
    () => ({
      title: `insights.episode_details_drawer.episode_details_sidebar.${activeView}.title`,
      description: `insights.episode_details_drawer.episode_details_sidebar.${activeView}.description`,
    }),
    [activeView]
  );

  const hasCopy = useMemo(() => activeView === 'speakers', [activeView]);

  return (
    <Sidebar
      header={null}
      customStyles={!isLoading && !segmentsWithParagraphs.length ? tw`opacity-40` : undefined}
      headerStyles={tw`hidden`}>
      {isLoading ? (
        <>
          <Spacer direction='vertical' size='sm' />
          <Loader theme='dark' size='md' />
        </>
      ) : (
        <>
          <EpisodeTranscriptControls />

          {!hasCopy && (
            <>
              <Separator />
              <Title>{t(episodeDetailsSidebarCopy.title)}</Title>
              <Description>{t(episodeDetailsSidebarCopy.description)}</Description>
            </>
          )}

          {activeView == 'garm_brand_safety' && <EpisodeGarmSection />}
          {activeView == 'iab_categories' && <EpisodeContextualSection />}
          {activeView == 'entities' && <EpisodeEntitiesSection />}
        </>
      )}
    </Sidebar>
  );
};
